// @ts-check
import { memo, useContext, useMemo, useState, useRef } from "react"
import {
  BarChart, Bar,
  LineChart, Line,
  AreaChart, Area,
  PieChart, Pie, Cell,
  ScatterChart, Scatter,
  ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from "recharts"
import { Download } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { parseChartXml } from "./parseChartXml"
import { ChatBubbleStreamingContext } from "./ChatBubbleStreamingContext"
import { toPng } from "html-to-image"

const COLORS = [
  "#ff6f61", "#ffb74d", "#a78bfa",
  "#77bfa3", "#2c7a7b", "#50c6a7",
  "#3a5a98", "#5dadec", "#66c2ff",
  "#ff8ba0", "#ff7043", "#ffd166",
  "#14b8a6", "#ffc947", "#9b5de5",
  "#4c6ef5", "#2bc16d", "#ffa07a",
]

/** @param {string} str */
function hashStr(str) {
  let h = 0
  for (let i = 0; i < str.length; i++) {
    h = (Math.imul(31, h) + str.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}

/**
 * Fisher-Yates shuffle seeded with a simple LCG. Same seed → same order.
 * @param {string[]} arr
 * @param {number} seed
 */
function seededShuffle(arr, seed) {
  const result = [...arr]
  let s = seed
  for (let i = result.length - 1; i > 0; i--) {
    s = (Math.imul(1664525, s) + 1013904223) | 0
    const j = Math.abs(s) % (i + 1)
    ;[result[i], result[j]] = [result[j], result[i]]
  }
  return result
}

/** @param {string} title */
function getColors(title) {
  return seededShuffle(COLORS, hashStr(title))
}

const Y_AXIS_WIDTH = 55
const MARGIN = { top: 10, right: Y_AXIS_WIDTH, left: 0, bottom: 30 }

const AXIS_STYLE = {
  tick: { fill: "currentColor", opacity: 0.4, fontSize: 11 },
  axisLine: { stroke: "currentColor", strokeOpacity: 0.15 },
  tickLine: false,
}

const GRID_STYLE = { strokeDasharray: "3 3", stroke: "currentColor", strokeOpacity: 0.1 }

const TOOLTIP_CURSOR = { fill: "currentColor", fillOpacity: 0.06 }

/** @type {{ angle: number, position: "insideLeft", style: { textAnchor: "middle" } }} */
const Y_LABEL_PROPS = { angle: -90, position: "insideLeft", style: { textAnchor: "middle" } }


// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

/** @param {{ height: number }} props */
function ChartSkeleton({ height }) {
  return (
    <div
      className="w-full rounded-lg bg-muted/40 animate-pulse my-4"
      style={{ height }}
    />
  )
}

// ---------------------------------------------------------------------------
// Error fallback
// ---------------------------------------------------------------------------

/** @param {{ title: string, error?: string | null, raw?: string }} props */
function ChartErrorCard({ title, error, raw }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="w-full rounded-lg border border-border bg-card p-4 my-4 space-y-2">
      <p className="text-sm font-medium">{title}</p>
      {error && (
        <p className="text-xs text-red-500 font-mono select-all">{error}</p>
      )}
      {raw && (
        <div>
          <button
            type="button"
            className="text-xs text-muted-foreground underline underline-offset-2"
            onClick={() => setOpen(v => !v)}
          >
            {open ? "Hide data" : "Show data"}
          </button>
          {open && (
            <pre className="mt-2 text-xs bg-muted rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
              {raw}
            </pre>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Individual chart type renderers
// ---------------------------------------------------------------------------

/** @typedef {{ config: import("./parseChartXml").ChartConfig }} RendererProps */

/**
 * @param {import("./parseChartXml").ChartPayload['labels']} labels
 * @param {import("./parseChartXml").ChartPayload['datasets']} datasets
 */
function buildSeriesData(labels, datasets) {
  return (labels ?? []).map((label, i) => ({
    name: label,
    ...Object.fromEntries(
      datasets.map(ds => [ds.label ?? "value", ds.data?.[i] ?? null])
    ),
  }))
}

/**
 * Renders the x-axis name with the series legend centered directly below it,
 * instead of recharts' default stacked label + legend (which drift out of alignment).
 * @param {{ xLabel?: string | null, series: { label: string, color: string }[] }} props
 */
function ChartLegendFooter({ xLabel, series }) {
  if (!xLabel && series.length === 0) return null
  return (
    <div className="flex flex-col items-center justify-center gap-1 mt-1 text-xs">
      {xLabel && <span className="font-medium text-foreground/70">{xLabel}</span>}
      {series.length > 0 && (
        <div className="flex items-center justify-center gap-3 flex-wrap">
          {series.map((s) => (
            <span key={s.label} className="inline-flex items-center gap-1.5 text-foreground/70">
              <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: s.color }} />
              {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

/** @param {RendererProps} props */
function BarChartRenderer({ config }) {
  const { payload, xLabel, yLabel, stacked, height, title } = config
  const colors = getColors(title)
  const data = buildSeriesData(payload.labels, payload.datasets)
  const readyDatasets = payload.datasets.filter(ds => Array.isArray(ds.data))

  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={MARGIN}>
          <CartesianGrid {...GRID_STYLE} />
          <XAxis dataKey="name" {...AXIS_STYLE} />
          <YAxis width={Y_AXIS_WIDTH} {...AXIS_STYLE} label={yLabel ? { value: yLabel, ...Y_LABEL_PROPS } : undefined} />
          <Tooltip cursor={TOOLTIP_CURSOR} />
          {readyDatasets.map((ds, i) => (
            <Bar
              key={ds.label ?? i}
              dataKey={ds.label ?? "value"}
              fill={colors[i % colors.length]}
              stackId={stacked ? "stack" : undefined}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <ChartLegendFooter xLabel={xLabel} series={readyDatasets.map((ds, i) => ({ label: ds.label ?? "value", color: colors[i % colors.length] }))} />
    </div>
  )
}

/** @param {RendererProps} props */
function LineChartRenderer({ config }) {
  const { payload, xLabel, yLabel, height, title } = config
  const colors = getColors(title)
  const data = buildSeriesData(payload.labels, payload.datasets)
  const readyDatasets = payload.datasets.filter(ds => Array.isArray(ds.data))

  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={MARGIN}>
          <CartesianGrid {...GRID_STYLE} />
          <XAxis dataKey="name" {...AXIS_STYLE} />
          <YAxis width={Y_AXIS_WIDTH} {...AXIS_STYLE} label={yLabel ? { value: yLabel, ...Y_LABEL_PROPS } : undefined} />
          <Tooltip cursor={TOOLTIP_CURSOR} />
          {readyDatasets.map((ds, i) => (
            <Line
              key={ds.label ?? i}
              type="monotone"
              dataKey={ds.label ?? "value"}
              stroke={colors[i % colors.length]}
              dot={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <ChartLegendFooter xLabel={xLabel} series={readyDatasets.map((ds, i) => ({ label: ds.label ?? "value", color: colors[i % colors.length] }))} />
    </div>
  )
}

/** @param {RendererProps} props */
function AreaChartRenderer({ config }) {
  const { payload, xLabel, yLabel, stacked, height, title } = config
  const colors = getColors(title)
  const data = buildSeriesData(payload.labels, payload.datasets)
  const readyDatasets = payload.datasets.filter(ds => Array.isArray(ds.data))

  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={MARGIN}>
          <CartesianGrid {...GRID_STYLE} />
          <XAxis dataKey="name" {...AXIS_STYLE} />
          <YAxis width={Y_AXIS_WIDTH} {...AXIS_STYLE} label={yLabel ? { value: yLabel, ...Y_LABEL_PROPS } : undefined} />
          <Tooltip cursor={TOOLTIP_CURSOR} />
          {readyDatasets.map((ds, i) => (
            <Area
              key={ds.label ?? i}
              type="monotone"
              dataKey={ds.label ?? "value"}
              stroke={colors[i % colors.length]}
              fill={colors[i % colors.length]}
              fillOpacity={0.2}
              stackId={stacked ? "stack" : undefined}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
      <ChartLegendFooter xLabel={xLabel} series={readyDatasets.map((ds, i) => ({ label: ds.label ?? "value", color: colors[i % colors.length] }))} />
    </div>
  )
}

/** @param {RendererProps & { donut?: boolean }} props */
function PieChartRenderer({ config, donut = false }) {
  const { payload, height, title } = config
  const colors = getColors(title)
  const firstDataset = payload.datasets[0]
  const data = (payload.labels ?? []).map((label, i) => ({
    name: label,
    value: firstDataset?.data?.[i] ?? 0,
  }))

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart margin={MARGIN}>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={donut ? "40%" : 0}
          outerRadius="70%"
          label
        >
          {data.map((_, i) => (
            <Cell key={i} fill={colors[i % colors.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend align="center" verticalAlign="bottom" wrapperStyle={{ paddingTop: 16 }} />
      </PieChart>
    </ResponsiveContainer>
  )
}

/** @param {RendererProps} props */
function ScatterChartRenderer({ config }) {
  const { payload, xLabel, yLabel, height, title } = config
  const colors = getColors(title)

  const readyDatasets = payload.datasets.filter(ds => Array.isArray(ds.data))

  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <ScatterChart margin={MARGIN}>
          <CartesianGrid {...GRID_STYLE} />
          <XAxis
            dataKey="x"
            type="number"
            name={xLabel ?? "x"}
            {...AXIS_STYLE}
          />
          <YAxis
            width={Y_AXIS_WIDTH}
            dataKey="y"
            type="number"
            name={yLabel ?? "y"}
            {...AXIS_STYLE}
            label={yLabel ? { value: yLabel, ...Y_LABEL_PROPS } : undefined}
          />
          <Tooltip cursor={TOOLTIP_CURSOR} />
          {readyDatasets.map((ds, i) => (
            <Scatter
              key={ds.label ?? i}
              name={ds.label ?? `Series ${i + 1}`}
              data={/** @type {any} */ (ds.data)}
              fill={colors[i % colors.length]}
            />
          ))}
        </ScatterChart>
      </ResponsiveContainer>
      <ChartLegendFooter xLabel={xLabel} series={readyDatasets.map((ds, i) => ({ label: ds.label ?? `Series ${i + 1}`, color: colors[i % colors.length] }))} />
    </div>
  )
}

/** @param {RendererProps} props */
function ComposedChartRenderer({ config }) {
  const { payload, xLabel, yLabel, height, title } = config
  const colors = getColors(title)
  const data = buildSeriesData(payload.labels, payload.datasets)
  const readyDatasets = payload.datasets.filter(ds => Array.isArray(ds.data))

  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={MARGIN}>
          <CartesianGrid {...GRID_STYLE} />
          <XAxis dataKey="name" {...AXIS_STYLE} />
          <YAxis width={Y_AXIS_WIDTH} {...AXIS_STYLE} label={yLabel ? { value: yLabel, ...Y_LABEL_PROPS } : undefined} />
          <Tooltip cursor={TOOLTIP_CURSOR} />
          {readyDatasets.map((ds, i) => {
            const key = ds.label ?? "value"
            const color = colors[i % colors.length]
            if (ds.type === "line") {
              return <Line key={key} type="monotone" dataKey={key} stroke={color} dot={false} />
            }
            return <Bar key={key} dataKey={key} fill={color} />
          })}
        </ComposedChart>
      </ResponsiveContainer>
      <ChartLegendFooter xLabel={xLabel} series={readyDatasets.map((ds, i) => ({ label: ds.label ?? "value", color: colors[i % colors.length] }))} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Chart type dispatch
// ---------------------------------------------------------------------------

/**
 * Memoised: recharts starts a series' animation again each time the series renders with new props,
 * so the chart must not render again while the rest of the message streams in.
 * @param {{ config: import("./parseChartXml").ChartConfig }} props
 */
const ChartSwitch = memo(function ChartSwitch({ config }) {
  switch (config.type) {
    case "bar":      return <BarChartRenderer config={config} />
    case "line":     return <LineChartRenderer config={config} />
    case "area":     return <AreaChartRenderer config={config} />
    case "pie":      return <PieChartRenderer config={config} />
    case "donut":    return <PieChartRenderer config={config} donut />
    case "scatter":  return <ScatterChartRenderer config={config} />
    case "composed": return <ComposedChartRenderer config={config} />
    default:         return null
  }
})

// ---------------------------------------------------------------------------
// Chart export — PNG only
// ---------------------------------------------------------------------------
//
// Uses html-to-image to capture the rendered DOM as-is, preserving Tailwind
// styles, legends, titles, and backgrounds exactly as displayed.

/** @param {string} dataUrl @param {string} filename */
function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement("a")
  a.href = dataUrl
  a.download = filename
  a.click()
}

/** @param {{ chartRef: React.RefObject<HTMLDivElement>, config: import("./parseChartXml").ChartConfig }} props */
function ChartExportButton({ chartRef, config }) {
  const handleExport = async () => {
    const container = chartRef.current
    if (!container) return

    try {
      await new Promise(resolve => requestAnimationFrame(resolve))

      const dataUrl = await toPng(container, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: getComputedStyle(document.body).backgroundColor || undefined,
        width: container.offsetWidth,
        height: container.offsetHeight,
        style: { margin: "0", padding: "0" },
      })

      const filename = `${config.title?.trim() || "chart"}.png`
      downloadDataUrl(dataUrl, filename)
    } catch (err) {
      console.error("Chart PNG export failed:", err)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Export chart"
          data-testid="chart-export-btn"
          className="absolute top-2 right-2 z-10 p-1.5 rounded-md bg-background/80 border border-border text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:text-foreground"
        >
          <Download size={14} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={handleExport}>Download as PNG</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ---------------------------------------------------------------------------
// Public component — registered in MARKDOWN_COMPONENTS as `chart`
// ---------------------------------------------------------------------------

/**
 * Receives all <chart> XML attributes as props and the JSON body as children.
 * react-markdown + rehype-raw call this automatically for every <chart> tag.
 */
/** @param {{ children?: unknown, node?: unknown, [key: string]: unknown }} props */
export default function ChartRenderer({ children, node, ...props }) {
  const isStreaming = useContext(ChatBubbleStreamingContext)
  const rawChildren = typeof children === "string" ? children : ""
  const chartRef = useRef(/** @type {HTMLDivElement | null} */ (null))

  // Every streamed chunk renders the message again. Keep the same config object while the
  // chart's own text is unchanged, so that the memoised ChartSwitch does not render again.
  const attributes = JSON.stringify(props)
  const result = useMemo(
    () => parseChartXml(JSON.parse(attributes), rawChildren),
    [attributes, rawChildren]
  )
  const height = props.height ? Number(props.height) : 320

  if (result == null) {
    if (isStreaming) return <ChartSkeleton height={height} />
    return null
  }

  if ("error" in result) {
    if (isStreaming) return <ChartSkeleton height={height} />
    return <ChartErrorCard title={result.title} error={result.error} raw={rawChildren} />
  }

  const config = result

  return (
    <div className="my-4 w-full">
      {config.title && (
        <p className="text-sm font-medium text-center mb-2 text-foreground/80">
          {config.title}
        </p>
      )}
      <div className="group relative" ref={chartRef}>
        <ChartExportButton chartRef={chartRef} config={config} />
        <ChartSwitch config={config} />
      </div>
    </div>
  )
}
